import "./PagesTranslationsView.styl";
import { supabaseClient } from "../auth/supabase.service.js";
import { holdButton } from "../_resources/ui-library.js";
import LOCALES from "../_resources/locales.js";

const { observable, autorun } = mobx;

// Helper to get locale info from LOCALES
function getLocaleInfo(localeCode) {
  return LOCALES.find(l => l.locale === localeCode) || null;
}

export default function ({ dom, message }) {
  const { bad, info, progress, good } = message;

  // Model
  const TranslationModel = {
    async fetchLocales(pageId) {
      const { data, error } = await supabaseClient
        .from('pages')
        .select('locales')
        .eq('id', pageId)
        .single();
      
      if (error) return { data: null, error };
      return { data: data?.locales || [], error: null };
    },

    async updateLocales(pageId, locales) {
      const { error } = await supabaseClient
        .from('pages')
        .update({ locales })
        .eq('id', pageId);
      
      return { error };
    },

    async fetchTranslations(scope, pageId) {
      const { data, error } = await supabaseClient
        .from('translations')
        .select('*')
        .eq('scope', scope)
        .eq('page_id', pageId);
      
      if (error) return { data: null, error };
      return { data: data || [], error: null };
    },

    async updateTranslation(scope, locale, pageId, spec, note) {
      const { error } = await supabaseClient
        .from('translations')
        .update({ spec, note })
        .eq('scope', scope)
        .eq('locale', locale)
        .eq('page_id', pageId);
      
      return { error };
    },

    async createTranslation(scope, locale, pageId, spec, note) {
      const { data, error } = await supabaseClient
        .from('translations')
        .insert([{ scope, locale, page_id: pageId, spec, note }])
        .select();
      
      return { data: data?.[0], error };
    },

    async deleteTranslation(scope, locale, pageId) {
      const { error } = await supabaseClient
        .from('translations')
        .delete()
        .eq('scope', scope)
        .eq('locale', locale)
        .eq('page_id', pageId);
      
      return { error };
    }
  };

  // State
  const state = observable({
    pageId: null,
    scope: 'page', // 'page' or 'vizabi'
    locales: [],
    translations: {},
    referenceLocale: null,
    editLocale: null,
    searchQuery: '',
    editingKey: null
  });

  // Non-observable editing state (no need to trigger re-renders on typing)
  let editingValue = '';

  // Controller
  const TranslationController = {
    async init(pageId, site, slug, hasPageEditAccess) {
      state.pageId = pageId;
      state.site = site;
      state.slug = slug;
      state.hasPageEditAccess = hasPageEditAccess;
      await this.loadLocales();
      await this.loadTranslations();
    },

    async loadLocales() {
      progress('Loading locales...');
      const { data, error } = await TranslationModel.fetchLocales(state.pageId);
      if (error) {
        bad('Error loading locales');
        console.error(error);
        return;
      }
      state.locales = data;
      if (data.length > 0) {
        state.referenceLocale = state.referenceLocale || data[0];
        state.editLocale = state.editLocale || data[0];
      }
      good('Locales loaded', 2000);
    },

    async loadTranslations() {
      progress('Loading translations...');
      const { data, error } = await TranslationModel.fetchTranslations(state.scope, state.pageId);
      if (error) {
        bad('Error loading translations');
        console.error(error);
        return;
      }
      
      state.translations = {};
      data.forEach(t => {
        state.translations[t.locale] = {
          id: t.id,
          spec: t.spec
        };
      });
      good('Translations loaded', 2000);
    },

    async addLocale(locale) {
      if (!locale || state.locales.includes(locale)) return;
      const newLocales = [...state.locales, locale];
      const { error } = await TranslationModel.updateLocales(state.pageId, newLocales);
      if (error) {
        bad('Error adding locale');
        console.error(error);
        return;
      }
      await this.loadLocales();
    },

    async removeLocale(locale) {
      const newLocales = state.locales.filter(l => l !== locale);
      const { error } = await TranslationModel.updateLocales(state.pageId, newLocales);
      if (error) {
        bad('Error removing locale');
        console.error(error);
        return;
      }
      if (state.referenceLocale === locale) state.referenceLocale = newLocales[0];
      if (state.editLocale === locale) state.editLocale = newLocales[0];
      await this.loadLocales();
    },

    async moveLocale(locale, direction) {
      const index = state.locales.indexOf(locale);
      if (index === -1) return;
      if (direction === 'up' && index === 0) return;
      if (direction === 'down' && index === state.locales.length - 1) return;
      
      const newLocales = [...state.locales];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      [newLocales[index], newLocales[newIndex]] = [newLocales[newIndex], newLocales[index]];
      
      const { error } = await TranslationModel.updateLocales(state.pageId, newLocales);
      if (error) {
        bad('Error reordering locales');
        console.error(error);
        return;
      }
      await this.loadLocales();
    },

    async updateTranslationKey(key, value) {
      const locale = state.editLocale;
      const translation = state.translations[locale];
      
      let spec = translation?.spec || {};
      
      if (state.scope === 'vizabi') {
        spec = {
          rtl: spec.rtl || false,
          currency: spec.currency || 'usd',
          dictionary: spec.dictionary || {}
        };
        spec.dictionary[key] = value;
      } else {
        spec[key] = value;
      }
      
      if (translation?.id) {
        const note = `${state.site}/${state.slug}`;
        const { error } = await TranslationModel.updateTranslation(state.scope, locale, state.pageId, spec, note);
        if (error) {
          bad('Error updating translation');
          console.error(error);
          return false;
        }
      } else {
        const note = `${state.site}/${state.slug}`;
        const { data, error } = await TranslationModel.createTranslation(state.scope, locale, state.pageId, spec, note);
        if (error) {
          bad('Error creating translation');
          console.error(error);
          return false;
        }
      }
      
      await this.loadTranslations();
      return true;
    },

    changeScope(newScope) {
      state.scope = newScope;
      this.loadTranslations();
    },

    startEdit(key, currentValue) {
      editingValue = currentValue || '';
      state.editingKey = key;
    },

    cancelEdit() {
      state.editingKey = null;
      editingValue = '';
    },

    async applyEdit() {
      const success = await this.updateTranslationKey(state.editingKey, editingValue);
      if (success) {
        this.cancelEdit();
      }
    },

    async addNewKey(key) {
      if (!key || key.trim() === '') {
        bad('Key name cannot be empty');
        return false;
      }

      // Validate key format: alphanumeric with _ and - only
      if (!/^[a-zA-Z0-9_\-\/]+$/.test(key)) {
        bad('Key must be alphanumeric with _ - / only');
        return false;
      }

      // Check if key already exists in any locale
      const exists = state.locales.some(locale => {
        const dict = getDictionary(state.translations[locale]);
        return dict.hasOwnProperty(key);
      });

      if (exists) {
        bad('Key already exists');
        return false;
      }

      // Add key to the current edit locale with empty value
      const success = await this.updateTranslationKey(key, '');
      if (success) {
        good('Key added successfully');
        return true;
      }
      return false;
    },

    async deleteKey(key) {
      progress('Deleting key from all locales...');
      let hasError = false;

      // Delete key from all locales
      for (const locale of state.locales) {
        const translation = state.translations[locale];
        if (!translation?.spec) continue;

        let spec = { ...translation.spec };
        
        if (state.scope === 'vizabi') {
          if (spec.dictionary && spec.dictionary.hasOwnProperty(key)) {
            spec = {
              rtl: spec.rtl || false,
              currency: spec.currency || 'usd',
              dictionary: { ...spec.dictionary }
            };
            delete spec.dictionary[key];
          } else {
            continue; // Key doesn't exist in this locale
          }
        } else {
          if (spec.hasOwnProperty(key)) {
            spec = { ...spec };
            delete spec[key];
          } else {
            continue; // Key doesn't exist in this locale
          }
        }

        const note = `${state.site}/${state.slug}`;
        const { error } = await TranslationModel.updateTranslation(state.scope, locale, state.pageId, spec, note);
        if (error) {
          console.error(`Error deleting key from locale ${locale}:`, error);
          hasError = true;
        }
      }

      if (hasError) {
        bad('Error deleting key from some locales');
        await this.loadTranslations();
        return false;
      } else {
        good('Key deleted from all locales');
        await this.loadTranslations();
        return true;
      }
    }
  };

  // Helper function to extract dictionary based on scope
  function getDictionary(translation) {
    if (!translation?.spec) return {};
    if (state.scope === 'vizabi') {
      return translation.spec.dictionary || {};
    }
    return translation.spec || {};
  }

  // View
  function render() {
    dom.html('');
    // Section 1: Locales list
    const localesSection = dom.append('section').class('translation-locales-section');
    
    renderLocalesList(localesSection);

    // Section 2: Scope selector and search
    const topSection = dom.append('section').class('translation-top-section admin-section-header');
    
    const scopeGroup = topSection.append('div').class('scope-selector');
    scopeGroup.append('h1').text('Text Editor');
    
    ['page', 'vizabi'].forEach(scope => {
      const radio = scopeGroup.append('input')
        .attr('type', 'radio')
        .attr('name', 'scope')
        .attr('id', `scope-${scope}`)
        .property('checked', state.scope === scope)
        .on('change', () => TranslationController.changeScope(scope));
      
      scopeGroup.append('label')
        .attr('for', `scope-${scope}`)
        .text(scope);
    });

    const searchGroup = topSection.append('div').class('search-group');
    searchGroup.append('label').text('Search: ');
    const searchInput = searchGroup.append('input')
      .attr('type', 'text')
      .class('input')
      .attr('placeholder', 'Search keys or values...')
      .on('input', function() {
        state.searchQuery = this.value.toLowerCase();
      });
    
    const addKeyBtn = searchGroup.append('button')
      .class('button addnew hold-btn')
      .classed('disabled', !state.hasPageEditAccess)
      .text('✚ New Text');
    
    holdButton(addKeyBtn, () => {
      const key = searchInput.node().value.trim();
      if (key) {
        TranslationController.addNewKey(key).then(success => {
          if (success) {
            console.log("Added new key:", key);
          }
        });
      }
    });



    // Section 3: Translation editor
    const editorSection = dom.append('section').class('translation-editor-section');
    
    autorun(() => {
      editorSection.html('');
      
      if (state.locales.length === 0) {
        editorSection.append('p').text('No locales available. Add a locale first.');
        return;
      }

      renderTranslationEditor(editorSection);
    });
  }

  function renderLocalesList(container) {
    const sectionHeader = container.append("div").class("admin-section-header");
    sectionHeader.append('h1').text('Translations');
    
    

    // Add locale form with validation and suggestions
    const addForm = sectionHeader.append('div').class('add-locale-form');

    // Validation message container
    const validationMsg = addForm.append('div')
      .class('validation-msg');

    const input = addForm.append('input')
      .attr('type', 'text')
      .class('input')
      .attr('placeholder', 'e.g., en, sv-SE');
    

    
    // Input validation on typing
    let isValidToAdd = false;
    input.on('input', function() {
      const value = this.value.trim();
      validationMsg.html('');
      isValidToAdd = false;
      
      if (!value) return;
      
      // Check if locale already exists
      if (state.locales.includes(value)) {
        validationMsg
          .class('validation-msg error')
          .text(`🚫 Already exists`);
        return;
      }
      
      const localeInfo = getLocaleInfo(value);
      if (localeInfo) {
        validationMsg
          .class('validation-msg success')
          .text(`✓ ${localeInfo.language.name} - ${localeInfo.country.name}`);
        isValidToAdd = true;
      } else {
        // Show suggestions
        const matches = LOCALES
          .filter(l => l.locale.toLowerCase().includes(value.toLowerCase()));
        
        if (matches.length > 0) {
          validationMsg
            .class('validation-msg suggestion')
            .text("Suggestions: "
            + (matches.slice(0, 5).map(m => m.locale).join(', '))
            + (matches.length > 5 ? ' and ' + matches.length + ' more...' : '')
          );
        } else {
          validationMsg
            .class('validation-msg error')
            .text('⚠ Unknown locale');
        }
      }
    });
    
    addForm.append('button')
      .class('button addnew')
      .classed('disabled', !state.hasPageEditAccess)
      .text('✚ New Locale')
      .on('click', () => {
        const locale = input.node().value.trim();
        if (locale && isValidToAdd) {
          TranslationController.addLocale(locale);
          input.node().value = '';
          validationMsg.html('');
        }
      });
    
    // Add on Enter key
    input.on('keypress', function(event) {
      if (event.key === 'Enter') {
        const locale = this.value.trim();
        if (locale && isValidToAdd) {
          TranslationController.addLocale(locale);
          this.value = '';
          validationMsg.html('');
        }
      }
    });

    const localesTable = container.append('table').class('admin-table locales-table');
    autorun(() => {
      localesTable.html('');
      
      const headerRow = localesTable.append('tr');
      headerRow.append('th').text('Order');
      headerRow.append('th').text('Locale');
      headerRow.append('th').text('Language');
      headerRow.append('th').text('Country');
      headerRow.append('th').text('Danger');

      if (state.locales.length === 0) {
        const emptyRow = localesTable.append('tr');
        emptyRow.append('td').attr('colspan', 5).class('empty-state').text('No locales');
      }

      state.locales.forEach((locale, index) => {
        const row = localesTable.append('tr');

        const orderCell = row.append('td').class('order-buttons');
        const buttonsDiv = orderCell.append('div').class('actionsbox');
        
        buttonsDiv.append('button')
          .class('button micro')
          .classed('disabled', !state.hasPageEditAccess)
          .text('▲')
          .property('disabled', index === 0)
          .on('click', () => TranslationController.moveLocale(locale, 'up'));
        
        buttonsDiv.append('button')
          .class('button micro')
          .classed('disabled', !state.hasPageEditAccess)
          .text('▼')
          .property('disabled', index === state.locales.length - 1)
          .on('click', () => TranslationController.moveLocale(locale, 'down'));

        row.append('td').text(locale);
        
        // Language column
        const localeInfo = getLocaleInfo(locale);
        row.append('td').text(localeInfo?.language?.name || '-');
        
        // Country column
        row.append('td').text(localeInfo?.country?.name || '-');
        
        
        const actionsCell = row.append('td');
        const deleteBtn = actionsCell.append('button')
          .class('button delete micro hold-btn')
          .classed('disabled', !state.hasPageEditAccess)
          .text('Remove');
        
        holdButton(deleteBtn, () => TranslationController.removeLocale(locale));
      });
    });

  }

  function renderTranslationEditor(container) {
    const controlsDiv = container.append('div').class('translation-controls');
    
    // Reference locale dropdown
    const leftHeader = controlsDiv.append("div").class("left-header");
    leftHeader.append('label').text('Reference: ');
    const refSelect = leftHeader.append('select').class('input');
    state.locales.forEach(locale => {
      refSelect.append('option')
        .attr('value', locale)
        .property('selected', locale === state.referenceLocale)
        .text(locale);
    });
    refSelect.on('change', function() {
      state.referenceLocale = this.value;
    });

    // Edit locale dropdown
    const rightHeader = controlsDiv.append("div").class("right-header");
    rightHeader.append('label').text('Edit: ');
    const editSelect = rightHeader.append('select').class('input');
    state.locales.forEach(locale => {
      editSelect.append('option')
        .attr('value', locale)
        .property('selected', locale === state.editLocale)
        .text(locale);
    });
    editSelect.on('change', function() {
      state.editLocale = this.value;
      state.editingKey = null;
    });

    // Translation table
    const tableWrapper = container.append('div')
      .class('translation-table-wrapper');

    autorun(() => {
      tableWrapper.html('');
      
      const table = tableWrapper.append('table').class('admin-table translation-table');
      
      const refTranslation = state.translations[state.referenceLocale];
      const editTranslation = state.translations[state.editLocale];
      
      const refDict = getDictionary(refTranslation);
      const editDict = getDictionary(editTranslation);
      
      // Get all unique keys
      const allKeys = new Set([...Object.keys(refDict), ...Object.keys(editDict)]);
      let keys = Array.from(allKeys).sort();

      // Apply search filter
      if (state.searchQuery) {
        keys = keys.filter(key => {
          const refValue = refDict[key] || '';
          const editValue = editDict[key] || '';
          return key.toLowerCase().includes(state.searchQuery) ||
                 refValue.toLowerCase().includes(state.searchQuery) ||
                 editValue.toLowerCase().includes(state.searchQuery);
        });
      }

      if (keys.length === 0) {
        const emptyRow = table.append('tr');
        emptyRow.append('td').attr('colspan', 3).class('empty-state').text('No translations found');
        return;
      }

      keys.forEach(key => {
        const row = table.append('tr').class('translation-row');
        
        // Reference column (with key)
        const refCell = row.append('td')
          .class('reference-cell');
        
        refCell.append('div')
          .class("key")
          .text(key);
        
        refCell.append('div')
          .class('reference-value' + (refDict[key] ? '' : 'not-set'))
          .text(refDict[key] || '(not set)');
        
        // Edit column (with key)
        const editCell = row.append('td')
          .class('editable-cell');
        
        // Always show key at top of edit cell
        editCell.append('div')
          .class("key")
          .text(key);

        if (state.editingKey === key) {
          // Show input and action buttons
          const inputWrapper = editCell.append('div');
          
          const textarea = inputWrapper.append('textarea')
            .class('input')
            .property('value', editingValue)
            .on('input', function() {
              editingValue = this.value;
            });
          
          const actionsDiv = inputWrapper.append('div').class('actions-div');
          
          actionsDiv.append('button')
            .class('button addnew')
            .classed('disabled', !state.hasPageEditAccess)
            .text('Apply')
            .on('click', () => TranslationController.applyEdit());
          
          actionsDiv.append('button')
            .class('button')
            .classed('disabled', !state.hasPageEditAccess)
            .text('Cancel')
            .on('click', () => TranslationController.cancelEdit());
        } else {
          // Show value as clickable text
          const value = editDict[key];
          const valueDiv = editCell.append('div')
            .class(value ? 'value-div' : 'value-div empty')
            .text(value || '(click to add)')
            .on('click', () => TranslationController.startEdit(key, value));
        }
        
        // Actions column - Reset and Delete buttons
        const actionsCell = row.append('td');
        
        // Reset button - copy reference to edit and apply
        const resetBtn = actionsCell.append('button')
          .class('button micro hold-btn')
          .classed('disabled', !state.hasPageEditAccess)
          .attr('title', 'Reset to reference value')
          .text('♻︎');

        holdButton(resetBtn, () => {
          const refValue = refDict[key] || '';
          TranslationController.startEdit(key, refValue);
          editingValue = refValue;
          TranslationController.applyEdit();
        });
        
        actionsCell.append('br');
        
        const deleteBtn = actionsCell.append('button')
          .class('button delete micro hold-btn')
          .classed('disabled', !state.hasPageEditAccess)
          .attr('title', 'Delete text from ALL locales')
          .text('✘');
        
        holdButton(deleteBtn, () => TranslationController.deleteKey(key));
      });
    });
  }

  return {
    render: (pageId, site, slug, hasPageEditAccess) => {
      TranslationController.init(pageId, site, slug, hasPageEditAccess).then(() => render());
    }
  };
}
